import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Upload,
  Download,
  Edit,
  Trash2,
  FileText,
  Calendar,
  DollarSign,
  Zap,
  X,
  Eye,
  Grid,
  List,
  Filter
  , ChevronUp, ChevronDown
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
  , Cell
} from 'recharts';
import { electricityAPI } from '../services/api';
import { formatCurrency, getMonthName, downloadFile } from '../utils/helpers';
import toast from 'react-hot-toast';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const ElectricityPage = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid' or 'chart'
  const [editingBill, setEditingBill] = useState(null);
  const [formData, setFormData] = useState({
    bill_month: new Date().getMonth() + 1,
    bill_year: new Date().getFullYear(),
    total_amount: '',
    total_units: '',
    current_meter_reading: '',
    previous_meter_reading: '',
    bill_pdf: null
  });
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterAmountRange, setFilterAmountRange] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchBills();
  }, []); // Remove filterYear dependency to fetch all bills initially

  // Auto-calculate total units when meter readings change
  useEffect(() => {
    if (formData.current_meter_reading && formData.previous_meter_reading) {
      const current = parseInt(formData.current_meter_reading);
      const previous = parseInt(formData.previous_meter_reading);
      if (current > previous) {
        setFormData(prev => ({
          ...prev,
          total_units: current - previous
        }));
      }
    }
  }, [formData.current_meter_reading, formData.previous_meter_reading]);

  const fetchBills = async () => {
    try {
      const response = await electricityAPI.getAll(); // Fetch all bills
      setBills(response.data.bills);
    } catch (error) {
      console.error('Error fetching bills:', error);
      toast.error('Failed to load electricity bills');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        bill_pdf: formData.bill_pdf
      };

      if (editingBill) {
        await electricityAPI.update(editingBill.id, submitData);
        toast.success('Electricity bill updated successfully');
      } else {
        await electricityAPI.create(submitData);
        toast.success('Electricity bill created successfully');
      }
      
      fetchBills();
      closeModal();
    } catch (error) {
      console.error('Error saving bill:', error);
      toast.error(error.response?.data?.error || 'Failed to save electricity bill');
    }
  };

  const handleDelete = (bill) => {
    setBillToDelete(bill);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await electricityAPI.delete(billToDelete.id);
      toast.success('Electricity bill deleted successfully');
      fetchBills();
    } catch (error) {
      console.error('Error deleting bill:', error);
      toast.error('Failed to delete electricity bill');
    }
  };


  const handleDownload = async (bill) => {
    try {
      const response = await electricityAPI.downloadPDF(bill.id);
      const filename = `electricity-bill-${bill.bill_year}-${bill.bill_month.toString().padStart(2, '0')}.pdf`;
      downloadFile(response.data, filename);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const openModal = (bill = null) => {
    if (bill) {
      setEditingBill(bill);
      setFormData({
        bill_month: bill.bill_month,
        bill_year: bill.bill_year,
        total_amount: bill.total_amount,
        total_units: bill.total_units,
        current_meter_reading: bill.current_meter_reading || '',
        previous_meter_reading: bill.previous_meter_reading || '',
        bill_pdf: null
      });
    } else {
      setEditingBill(null);
      setFormData({
        bill_month: new Date().getMonth() + 1,
        bill_year: new Date().getFullYear(),
        total_amount: '',
        total_units: '',
        current_meter_reading: '',
        previous_meter_reading: '',
        bill_pdf: null
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBill(null);
  };

  const openDetailModal = (bill) => {
    setSelectedBill(bill);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedBill(null);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'bill_pdf') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const clearFilters = () => {
    setFilterYear('');
    setFilterMonth('');
    setFilterAmountRange('');
  };

  const handleDownloadFilteredPDF = async () => {
    try {
      const filteredData = getFilteredBills();
      
      if (filteredData.length === 0) {
        toast.error('No bills to download');
        return;
      }

      // Create PDF content
      const totalAmount = filteredData.reduce((sum, bill) => sum + (bill.total_amount || 0), 0);
      const totalUnits = filteredData.reduce((sum, bill) => sum + (bill.total_units || 0), 0);
      
      const reportTitle = 'Electricity Bills Report';
      const filterInfo = [];
      if (filterYear) filterInfo.push(`Year: ${filterYear}`);
      if (filterMonth) filterInfo.push(`Month: ${getMonthName(parseInt(filterMonth))}`);
      if (filterAmountRange) filterInfo.push(`Amount: ${filterAmountRange}`);
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Electricity Bills Report - ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</title>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 40px;
              background: white;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 3px solid #3b82f6; 
              padding-bottom: 20px; 
            }
            .header h1 { 
              color: #1e40af; 
              margin: 0; 
              font-size: 28px; 
            }
            .header p { 
              color: #6b7280; 
              margin: 5px 0; 
            }
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #3b82f6;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 600;
              cursor: pointer;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              z-index: 1000;
              transition: background 0.3s;
            }
            .print-button:hover {
              background: #2563eb;
            }
            .filters { 
              background: #f3f4f6; 
              padding: 15px; 
              border-radius: 8px; 
              margin-bottom: 20px; 
            }
            .filters h3 { 
              margin: 0 0 10px 0; 
              color: #374151; 
              font-size: 16px; 
            }
            .filters p { 
              margin: 5px 0; 
              color: #6b7280; 
            }
            .summary { 
              display: grid; 
              grid-template-columns: repeat(3, 1fr); 
              gap: 15px; 
              margin-bottom: 30px; 
            }
            .summary-card { 
              background: #eff6ff; 
              padding: 15px; 
              border-radius: 8px; 
              text-align: center; 
              border-left: 4px solid #3b82f6; 
            }
            .summary-card h4 { 
              margin: 0; 
              color: #6b7280; 
              font-size: 14px; 
              font-weight: normal; 
            }
            .summary-card p { 
              margin: 10px 0 0 0; 
              color: #1e40af; 
              font-size: 24px; 
              font-weight: bold; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px; 
              font-size: 11px;
            }
            th { 
              background: #1e40af; 
              color: white; 
              padding: 8px 6px; 
              text-align: left; 
              font-weight: 600; 
              font-size: 11px;
            }
            td { 
              padding: 6px 6px; 
              border-bottom: 1px solid #e5e7eb; 
              font-size: 11px;
            }
            tr:nth-child(even) { 
              background: #f9fafb; 
            }
            tr:hover { 
              background: #f3f4f6; 
            }
            .footer { 
              margin-top: 30px; 
              text-align: center; 
              color: #9ca3af; 
              font-size: 12px; 
              border-top: 1px solid #e5e7eb; 
              padding-top: 20px; 
            }
            @media print {
              .print-button {
                display: none;
              }
              body {
                padding: 0;
              }
              .summary-card,
              .filters,
              tr:nth-child(even) {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <button class="print-button" onclick="window.print()">🖨️ Print / Save as PDF</button>
          
          <div class="header">
            <h1>${reportTitle}</h1>
            <p>Generated on ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          
          ${filterInfo.length > 0 ? `
          <div class="filters">
            <h3>Applied Filters</h3>
            ${filterInfo.map(info => `<p>${info}</p>`).join('')}
          </div>
          ` : ''}
          
          <div class="summary">
            <div class="summary-card">
              <h4>Total Bills</h4>
              <p>${filteredData.length}</p>
            </div>
            <div class="summary-card">
              <h4>Total Units</h4>
              <p>${Math.round(totalUnits)} kWh</p>
            </div>
            <div class="summary-card">
              <h4>Total Amount</h4>
              <p>${formatCurrency(totalAmount)}</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Period</th>
                <th>Units</th>
                <th>Amount</th>
                <th>Prev. Reading</th>
                <th>Curr. Reading</th>
                <th>Rate/Unit</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(bill => `
                <tr>
                  <td>${getMonthName(bill.bill_month).slice(0, 3)} ${bill.bill_year}</td>
                  <td>${bill.total_units || 0}</td>
                  <td>${formatCurrency(bill.total_amount || 0)}</td>
                  <td>${bill.previous_meter_reading || 'N/A'}</td>
                  <td>${bill.current_meter_reading || 'N/A'}</td>
                  <td>${bill.total_units > 0 ? formatCurrency((bill.total_amount || 0) / bill.total_units) : 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Tenant Bills Management System © ${new Date().getFullYear()}</p>
          </div>
        </body>
        </html>
      `;

      // Open HTML in new window/tab
      const newWindow = window.open('', '_blank');
      newWindow.document.open();
      newWindow.document.write(htmlContent);
      newWindow.document.close();
      
      // Ensure title is set
      setTimeout(() => {
        if (newWindow && !newWindow.closed) {
          newWindow.document.title = `Electricity Bills Report - ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}`;
        }
      }, 100);
      
      toast.success('Report opened in new tab! Click the print button to save as PDF.');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate report');
    }
  };

  const getFilteredBills = () => {
    return bills.filter(bill => {
      // Year filter
      if (filterYear && bill.bill_year !== parseInt(filterYear)) {
        return false;
      }

      // Month filter
      if (filterMonth && bill.bill_month !== parseInt(filterMonth)) {
        return false;
      }

      // Amount range filter
      if (filterAmountRange) {
        const amount = parseFloat(bill.total_amount);
        switch (filterAmountRange) {
          case '0-1000':
            if (amount < 0 || amount > 1000) return false;
            break;
          case '1000-2500':
            if (amount < 1000 || amount > 2500) return false;
            break;
          case '2500-5000':
            if (amount < 2500 || amount > 5000) return false;
            break;
          case '5000+':
            if (amount < 5000) return false;
            break;
          default:
            break;
        }
      }

      return true;
    });
  };

  const filteredBills = getFilteredBills();

  const sortedFilteredBills = React.useMemo(() => {
    const list = filteredBills.slice();
    if (!sortConfig.key) return list;

    const dir = sortConfig.direction === 'asc' ? 1 : -1;

    return list.sort((a, b) => {
      switch (sortConfig.key) {
        case 'period': {
          if (a.bill_year !== b.bill_year) return (a.bill_year - b.bill_year) * dir;
          return (a.bill_month - b.bill_month) * dir;
        }
        case 'total_amount': {
          const av = Number(a.total_amount) || 0;
          const bv = Number(b.total_amount) || 0;
          return (av - bv) * dir;
        }
        case 'meter_reading': {
          const av = Number(a.current_meter_reading ?? a.previous_meter_reading ?? 0);
          const bv = Number(b.current_meter_reading ?? b.previous_meter_reading ?? 0);
          return (av - bv) * dir;
        }
        case 'units': {
          const av = Number(a.total_units) || 0;
          const bv = Number(b.total_units) || 0;
          return (av - bv) * dir;
        }
        case 'unit_rate': {
          const av = Number(a.unit_rate ?? ((a.total_units > 0) ? (a.total_amount / a.total_units) : 0)) || 0;
          const bv = Number(b.unit_rate ?? ((b.total_units > 0) ? (b.total_amount / b.total_units) : 0)) || 0;
          return (av - bv) * dir;
        }
        default:
          return 0;
      }
    });
  }, [filteredBills, sortConfig]);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline-block ml-2" />
    ) : (
      <ChevronDown className="w-3 h-3 inline-block ml-2" />
    );
  };

  // Chart data (sorted by year then month)
  // Aggregate bills by period (month + year) so charts and consumption card show one row per period
  const aggregated = filteredBills.reduce((map, b) => {
    const key = `${b.bill_year}-${String(b.bill_month).padStart(2, '0')}`;
    if (!map[key]) {
      map[key] = { bill_year: b.bill_year, bill_month: b.bill_month, total: 0, units: 0 };
    }
    map[key].total += Number(b.total_amount) || 0;
    map[key].units += Number(b.total_units) || 0;
    return map;
  }, {});

  const chartData = Object.values(aggregated)
    .sort((a, b) => (a.bill_year - b.bill_year) || (a.bill_month - b.bill_month))
    .map(d => ({ period: `${getMonthName(d.bill_month)} ${d.bill_year}`, total: d.total, units: d.units }));

  // State for consumption-style card (similar to Dashboard)
  // Consumption list derived from bills (period, total, units) — driven by top filters (filterYear/filterMonth)
  const consumptionListAll = Object.values(aggregated).map(d => ({
    period: `${getMonthName(d.bill_month)} ${d.bill_year}`,
    total: Number(d.total) || 0,
    units: Number(d.units) || 0,
    bill_year: d.bill_year,
    bill_month: d.bill_month
  })).sort((a, b) => {
    // Sort by year desc then month desc where possible by parsing period or using original filteredBills order
    // We'll attempt to extract year/month from the period string ("Month YYYY")
    // Use numeric year/month for reliable sorting (descending)
    if (a.bill_year !== b.bill_year) return b.bill_year - a.bill_year;
    return b.bill_month - a.bill_month;
  });

  // Use the filtered list directly for the consumption card
  const consumptionList = consumptionListAll;

  // Add unit cost calculation (total / units) for display in the consumption card
  const consumptionListWithCost = consumptionList.map(item => ({
    ...item,
    unitCost: item.units > 0 ? (item.total / item.units) : 0
  }));

  const maxTotalForBars = consumptionList.reduce((m, c) => Math.max(m, c.total || 0), 0) || 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Debug log to check viewMode
  console.log('ElectricityPage viewMode:', viewMode);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Electricity Bills</h1>
          <p className="text-secondary-600 dark:text-secondary-300">Manage monthly electricity bills and unit rates</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* View Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-secondary-300">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-500 text-white'
          : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <Grid className="w-4 h-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`px-3 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'chart'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Chart</span>
            </button>
          </div>
          
          <button
            onClick={() => openModal()}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Add Bill</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {bills.length > 0 && (
        <div className="card">
          <div className="flex items-center space-x-4">
            <Filter className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 flex-1">
              <div>
                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(Number(e.target.value))}
                  className="form-input"
                >
                  <option value="">All Years</option>
                  {generateYears().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  className="form-input"
                >
                  <option value="">All Months</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
              <div>
                <select
                  value={filterAmountRange}
                  onChange={(e) => setFilterAmountRange(e.target.value)}
                  className="form-input"
                >
                  <option value="">All Amounts</option>
                  <option value="0-1000">₹0 - ₹1,000</option>
                  <option value="1000-2500">₹1,000 - ₹2,500</option>
                  <option value="2500-5000">₹2,500 - ₹5,000</option>
                  <option value="5000+">₹5,000+</option>
                </select>
              </div>
              <div>
                <button
                  onClick={handleDownloadFilteredPDF}
                  className="btn-primary w-full flex items-center justify-center"
                  title="Download PDF Report"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </button>
              </div>
              <div>
                <button
                  onClick={clearFilters}
                  className="btn-outline w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bills Display */}
      {filteredBills.length > 0 ? (
        viewMode === 'list' ? (
          // List View - Modern Table
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-secondary-50 dark:bg-secondary-700/50 border-b border-secondary-200 dark:border-secondary-600">
                    <th onClick={() => setSortConfig(prev => ({ key: 'period', direction: prev.key === 'period' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Period {renderSortIcon('period')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'total_amount', direction: prev.key === 'total_amount' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Total Amount {renderSortIcon('total_amount')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'meter_reading', direction: prev.key === 'meter_reading' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Meter Readings {renderSortIcon('meter_reading')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'units', direction: prev.key === 'units' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Units Used {renderSortIcon('units')}
                    </th>
                    <th onClick={() => setSortConfig(prev => ({ key: 'unit_rate', direction: prev.key === 'unit_rate' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      className="cursor-pointer px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                      Unit Rate {renderSortIcon('unit_rate')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">PDF</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                  {sortedFilteredBills.map((bill) => (
                    <motion.tr
                      key={bill.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-700/30 transition-colors group cursor-pointer"
                      onClick={() => openDetailModal(bill)}
                    >
                      {/* Period */}
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                            <Zap className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-secondary-900 dark:text-secondary-100 text-sm">
                              {getMonthName(bill.bill_month)} {bill.bill_year}
                            </p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 mt-0.5">
                              Monthly Bill
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Total Amount */}
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          {formatCurrency(bill.total_amount)}
                        </span>
                      </td>

                      {/* Meter Readings */}
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2 text-sm">
                          <div className="text-center">
                            <span className="text-xs text-secondary-400 dark:text-secondary-500 block">Prev</span>
                            <span className="font-medium text-secondary-700 dark:text-secondary-300">{bill.previous_meter_reading ?? 'N/A'}</span>
                          </div>
                          <span className="text-secondary-300 dark:text-secondary-600">→</span>
                          <div className="text-center">
                            <span className="text-xs text-secondary-400 dark:text-secondary-500 block">Curr</span>
                            <span className="font-medium text-secondary-700 dark:text-secondary-300">{bill.current_meter_reading ?? 'N/A'}</span>
                          </div>
                        </div>
                      </td>

                      {/* Units Used */}
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          <Zap className="w-3 h-3 mr-1" />
                          {bill.total_units} kWh
                        </span>
                      </td>

                      {/* Unit Rate */}
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                          {formatCurrency(bill.unit_rate)}<span className="text-xs text-secondary-400 dark:text-secondary-500">/unit</span>
                        </span>
                      </td>

                      {/* PDF */}
                      <td className="px-6 py-4">
                        {bill.bill_pdf_path ? (
                          <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            <FileText className="w-3 h-3" />
                            <span>PDF</span>
                          </span>
                        ) : (
                          <span className="text-xs text-secondary-400 dark:text-secondary-500">—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                          {bill.bill_pdf_path && (
                            <button
                              onClick={() => handleDownload(bill)}
                              className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openDetailModal(bill)}
                            className="p-1.5 text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openModal(bill)}
                            className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            title="Edit Bill"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(bill)}
                            className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
                            title="Delete Bill"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          // Grid View - Modern Cards
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedFilteredBills.map((bill) => (
              <motion.div
                key={bill.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => openDetailModal(bill)}
              >
                {/* Gradient Header */}
                <div className="bg-gradient-to-r from-teal-600 to-cyan-700 p-5 relative">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-sm">
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white leading-tight">
                          {getMonthName(bill.bill_month)} {bill.bill_year}
                        </h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/25 text-white mt-1">
                          Monthly Bill
                        </span>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      {bill.bill_pdf_path && (
                        <button
                          onClick={() => handleDownload(bill)}
                          className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                          title="Download PDF"
                        >
                          <Download className="w-3.5 h-3.5 text-white" />
                        </button>
                      )}
                      <button
                        onClick={() => openModal(bill)}
                        className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        title="Edit Bill"
                      >
                        <Edit className="w-3.5 h-3.5 text-white" />
                      </button>
                      <button
                        onClick={() => handleDelete(bill)}
                        className="p-1.5 bg-white/20 hover:bg-red-500/50 rounded-lg transition-colors"
                        title="Delete Bill"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  </div>

                  {/* Amount hero */}
                  <div className="mt-4">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Total Amount</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(bill.total_amount)}</p>
                  </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                  {/* Meter readings */}
                  <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-3">
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold mb-2">Meter Readings</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-xs text-secondary-400 dark:text-secondary-500">Previous</span>
                        <p className="font-semibold text-secondary-800 dark:text-secondary-200">{bill.previous_meter_reading ?? 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-secondary-400 dark:text-secondary-500">Current</span>
                        <p className="font-semibold text-secondary-800 dark:text-secondary-200">{bill.current_meter_reading ?? 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Units & Rate badges */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center bg-amber-50 dark:bg-amber-900/20 rounded-xl py-3">
                      <p className="text-xs text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wide">Units</p>
                      <p className="text-lg font-bold text-amber-700 dark:text-amber-300">{bill.total_units}</p>
                      <p className="text-xs text-amber-500 dark:text-amber-500">kWh</p>
                    </div>
                    <div className="text-center bg-emerald-50 dark:bg-emerald-900/20 rounded-xl py-3">
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold uppercase tracking-wide">Rate</p>
                      <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(bill.unit_rate)}</p>
                      <p className="text-xs text-emerald-500 dark:text-emerald-500">per unit</p>
                    </div>
                  </div>

                  {/* PDF badge */}
                  <div className="flex items-center justify-between pt-1">
                    {bill.bill_pdf_path ? (
                      <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        <FileText className="w-3.5 h-3.5" />
                        <span>PDF Attached</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-400">
                        <FileText className="w-3.5 h-3.5" />
                        <span>No PDF</span>
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); openDetailModal(bill); }}
                      className="inline-flex items-center space-x-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Details</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          // Chart View - Modern Consumption Overview
          <div className="space-y-6">
            {/* KPI Summary Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5 flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold">Total Bills</p>
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{filteredBills.length}</p>
                </div>
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5 flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold">Total Units</p>
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{Math.round(filteredBills.reduce((s, b) => s + (b.total_units || 0), 0))} <span className="text-sm font-medium text-secondary-500">kWh</span></p>
                </div>
              </div>
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-5 flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold">Total Cost</p>
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{formatCurrency(filteredBills.reduce((s, b) => s + (b.total_amount || 0), 0))}</p>
                </div>
              </div>
            </div>

            {/* Consumption Bars Card */}
            <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-teal-600 to-cyan-700 px-6 py-5">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Electricity Consumption</h3>
                    <p className="text-xs text-white/75">Monthly breakdown based on bill data</p>
                  </div>
                </div>
              </div>

              {/* Bars */}
              <div className="p-6 space-y-5">
                {consumptionListWithCost.map((item, idx) => {
                  const perc = Math.round((item.total / maxTotalForBars) * 100);
                  const widthPerc = Math.max(perc, 4);
                  const lowThreshold = maxTotalForBars * 0.33;
                  const highThreshold = maxTotalForBars * 0.66;
                  let barClass = 'bg-gradient-to-r from-emerald-400 to-emerald-500';
                  if (item.total > highThreshold) barClass = 'bg-gradient-to-r from-amber-400 via-orange-400 to-red-500';
                  else if (item.total > lowThreshold) barClass = 'bg-gradient-to-r from-emerald-400 to-amber-400';

                  return (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center space-x-2">
                          <span className="w-6 h-6 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <Zap className="w-3 h-3 text-amber-600 dark:text-amber-400" />
                          </span>
                          <span className="font-semibold text-sm text-secondary-800 dark:text-secondary-200">{item.period}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-sm text-secondary-900 dark:text-secondary-100">{formatCurrency(item.total)}</span>
                          <span className="text-xs text-secondary-400 dark:text-secondary-500 ml-2">{item.units} kWh · {formatCurrency(item.unitCost)}/kWh</span>
                        </div>
                      </div>
                      <div className="w-full bg-secondary-100 dark:bg-secondary-700 rounded-full h-2.5">
                        <div
                          className={`${barClass} h-2.5 rounded-full transition-all duration-500`}
                          style={{ width: `${widthPerc}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="px-6 pb-5 flex items-center space-x-6 text-xs text-secondary-500 dark:text-secondary-400">
                <div className="flex items-center space-x-1.5"><span className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 inline-block"></span><span>Low</span></div>
                <div className="flex items-center space-x-1.5"><span className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-400 to-amber-400 inline-block"></span><span>Medium</span></div>
                <div className="flex items-center space-x-1.5"><span className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-400 to-red-500 inline-block"></span><span>High</span></div>
              </div>
            </div>
          </div>
        )
      ) : bills.length === 0 ? (
        <div className="text-center py-12">
          <Zap className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-secondary-100 mb-2">No electricity bills found</h3>
          <p className="text-secondary-600 dark:text-secondary-300 mb-4">Start by adding your first electricity bill</p>
          <button
            onClick={() => openModal()}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add First Bill
          </button>
        </div>
      ) : (
        <div className="text-center py-12">
          <Filter className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-secondary-100 mb-2">No bills match your filters</h3>
          <p className="text-secondary-600 dark:text-secondary-300 mb-4">Try adjusting your filter criteria or clear all filters</p>
          <button
            onClick={clearFilters}
            className="btn-outline"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-secondary-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                    {editingBill ? 'Edit Electricity Bill' : 'Add New Electricity Bill'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Month</label>
                      <select
                        name="bill_month"
                        value={formData.bill_month}
                        onChange={handleChange}
                        className="form-input"
                        required
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                          <option key={month} value={month}>
                            {getMonthName(month)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Year</label>
                      <select
                        name="bill_year"
                        value={formData.bill_year}
                        onChange={handleChange}
                        className="form-input"
                        required
                      >
                        {generateYears().map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Total Amount (£)</label>
                    <input
                      type="number"
                      name="total_amount"
                      value={formData.total_amount}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="250.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Previous Meter Reading</label>
                      <input
                        type="number"
                        name="previous_meter_reading"
                        value={formData.previous_meter_reading}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 12450"
                        min="0"
                      />
                      <p className="text-xs text-secondary-500 dark:text-secondary-300 mt-1">Reading from previous month</p>
                    </div>
                    <div>
                      <label className="form-label">Current Meter Reading</label>
                      <input
                        type="number"
                        name="current_meter_reading"
                        value={formData.current_meter_reading}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 13450"
                        min="0"
                      />
                      <p className="text-xs text-secondary-500 dark:text-secondary-300 mt-1">Reading for this month</p>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Total Units Consumed</label>
                    <input
                      type="number"
                      name="total_units"
                      value={formData.total_units}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Will auto-calculate from meter readings"
                      min="0"
                      required
                    />
                    <p className="text-xs text-secondary-500 dark:text-secondary-300 mt-1">
                      {formData.current_meter_reading && formData.previous_meter_reading && 
                       parseInt(formData.current_meter_reading) > parseInt(formData.previous_meter_reading) ? 
                        `Auto-calculated: ${parseInt(formData.current_meter_reading) - parseInt(formData.previous_meter_reading)} units` : 
                        'Enter manually or provide meter readings above'
                      }
                    </p>
                  </div>

                  <div>
                    <label className="form-label">
                      Bill PDF {editingBill && '(Upload new file to replace)'}
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="file"
                        name="bill_pdf"
                        onChange={handleChange}
                        className="form-input"
                        accept=".pdf"
                      />
                      <Upload className="w-5 h-5 text-secondary-400 dark:text-secondary-300" />
                    </div>
                    <p className="text-xs text-secondary-500 dark:text-secondary-300 mt-1">
                      PDF files only, max 10MB
                    </p>
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="btn-outline"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      {editingBill ? 'Update Bill' : 'Create Bill'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedBill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeDetailModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-secondary-200 dark:border-secondary-700"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gradient Header */}
              <div className="bg-gradient-to-r from-teal-600 to-cyan-700 rounded-t-2xl px-6 py-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-sm flex-shrink-0">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-white/70 uppercase tracking-widest font-semibold mb-0.5">Monthly Electricity Bill</p>
                      <h2 className="text-2xl font-bold text-white leading-tight">
                        {getMonthName(selectedBill.bill_month)} {selectedBill.bill_year}
                      </h2>
                    </div>
                  </div>
                  <button
                    onClick={closeDetailModal}
                    className="p-2 bg-white/15 hover:bg-white/25 rounded-xl transition-colors flex-shrink-0"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* 3 Hero Stat Boxes */}
                <div className="mt-5 grid grid-cols-3 gap-3">
                  <div className="bg-white/15 rounded-xl px-4 py-3">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Total Amount</p>
                    <p className="text-xl font-bold text-white mt-0.5">{formatCurrency(selectedBill.total_amount)}</p>
                  </div>
                  <div className="bg-white/15 rounded-xl px-4 py-3">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Units Consumed</p>
                    <p className="text-xl font-bold text-white mt-0.5">{selectedBill.total_units} <span className="text-sm font-medium">kWh</span></p>
                  </div>
                  <div className="bg-white/15 rounded-xl px-4 py-3">
                    <p className="text-xs text-white/70 uppercase tracking-wider">Unit Rate</p>
                    <p className="text-xl font-bold text-white mt-0.5">{formatCurrency(selectedBill.unit_rate)}<span className="text-sm font-medium">/unit</span></p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-6">

                {/* Meter Readings */}
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold mb-3">Meter Readings</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-4">
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 font-medium uppercase tracking-wide mb-1">Previous Reading</p>
                      <p className="text-2xl font-bold text-secondary-800 dark:text-secondary-100">
                        {selectedBill.previous_meter_reading || 'N/A'}
                      </p>
                    </div>
                    <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-4">
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 font-medium uppercase tracking-wide mb-1">Current Reading</p>
                      <p className="text-2xl font-bold text-secondary-800 dark:text-secondary-100">
                        {selectedBill.current_meter_reading || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Billing Details */}
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wider font-semibold mb-3">Billing Details</p>
                  <div className="space-y-3">
                    {/* Bill Period */}
                    <div className="flex items-center justify-between px-4 py-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
                      <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-300">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm font-medium">Bill Period</span>
                      </div>
                      <span className="text-sm font-semibold text-secondary-800 dark:text-secondary-100">
                        {getMonthName(selectedBill.bill_month)} {selectedBill.bill_year}
                      </span>
                    </div>

                    {/* Unit Rate row with auto-calc note */}
                    <div className="flex items-center justify-between px-4 py-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
                      <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-300">
                        <Zap className="w-4 h-4" />
                        <span className="text-sm font-medium">Unit Rate (Calculated)</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(selectedBill.unit_rate)}/unit
                        </span>
                        <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">
                          Auto-calculated: amount ÷ units
                        </p>
                      </div>
                    </div>

                    {/* PDF Document */}
                    <div className="flex items-center justify-between px-4 py-3 bg-secondary-50 dark:bg-secondary-700/40 rounded-xl">
                      <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-300">
                        <FileText className="w-4 h-4" />
                        <span className="text-sm font-medium">PDF Document</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {selectedBill.bill_pdf_path ? (
                          <>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              Available
                            </span>
                            <button
                              onClick={() => handleDownload(selectedBill)}
                              className="inline-flex items-center space-x-1 text-xs font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
                            >
                              <Download className="w-3.5 h-3.5" />
                              <span>Download</span>
                            </button>
                          </>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-400">
                            Not Attached
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rate Calculation Info Box */}
                <div className="px-4 py-3 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-100 dark:border-teal-800">
                  <p className="text-sm text-teal-800 dark:text-teal-300">
                    <strong>Rate Calculation:</strong> This unit rate ({formatCurrency(selectedBill.unit_rate)}/unit) applies to all meter readings for {getMonthName(selectedBill.bill_month)} {selectedBill.bill_year}.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 pt-2 border-t border-secondary-100 dark:border-secondary-700">
                  <button
                    onClick={closeDetailModal}
                    className="btn-outline"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      closeDetailModal();
                      openModal(selectedBill);
                    }}
                    className="btn-primary"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Bill
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        itemName={billToDelete ? `${getMonthName(billToDelete.bill_month)} ${billToDelete.bill_year} Electricity Bill` : ''}
        itemType="electricity bill"
      />
    </div>
  );
};

export default ElectricityPage;
